import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Subscription } from 'rxjs';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { EnterpriseAdminService } from 'src/app/modules/enterprise-admin/enterprise-admin.service';
import { CreateTaskDialogComponent } from '../create-task-dialog/create-task-dialog.component';
import {
  TaskActionDialogComponent,
  TaskActionDialogMode,
  TaskActionDialogResult,
} from '../task-action-dialog/task-action-dialog.component';
import {
  TaskDetailResponse,
  TaskListItem,
  TasksService,
  TaskSummary,
  TaskTab,
} from '../services/tasks.service';
import { formatStartTimeLabel } from '../utils/entity-leads.utils';
import {
  EffectiveTaskStatus,
  formatTaskDateTime,
  getEffectiveTaskStatus,
} from '../utils/task-status.util';
import {
  getTeamMemberDisplayName,
  getTeamMemberUserId,
} from '../utils/team-member.util';

type TasksTabFilter = '' | TaskTab;
type TasksDrawerTab = 'details' | 'activity';

@Component({
  selector: 'hour4u-admin-entity-tasks-page',
  templateUrl: './entity-tasks-page.component.html',
  styleUrls: ['./entity-tasks-page.component.scss']
})
export class EntityTasksPageComponent implements OnInit, OnDestroy {
  profile = this.appService.getCurrentAccount();

  projectsList: any[] = [];
  outcomeOptions: string[] = [];
  teamMembers: any[] = [];

  tasks: TaskListItem[] = [];
  summary: TaskSummary = {
    total: 0,
    awaiting_approval: 0,
    scheduled: 0,
    running: 0,
    completed: 0,
    paused: 0,
    failed: 0,
    cancelled: 0,
  };

  selectedProjectId = '';
  selectedChannel = '';
  selectedOwnerType = '';
  selectedOwnerId = '';
  selectedOutcome = '';
  selectedStatus = '';
  activeTab: TasksTabFilter = '';
  searchQuery = '';

  pageNum = 1;
  pageSize = 25;
  totalElements = 0;
  pageSizeOptions = [25, 50, 100];
  /** Server-reported paging state — preferred over anything derived locally. */
  serverTotalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;
  isApproximateTotal = false;

  isLoading = false;
  isLoadingProjects = false;
  isActionLoading = false;
  detailLoading = false;

  selectedTaskId: string | null = null;
  taskDetail: TaskDetailResponse | null = null;
  drawerOpen = false;
  drawerTab: TasksDrawerTab = 'details';
  candidateDetails: any = null;
  activityHistoryLoading = false;

  private searchTimeout: any;
  private subs = new Subscription();

  readonly channelOptions = [
    { value: '', label: 'All channels' },
    { value: 'call', label: 'Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Email' },
    { value: 'human', label: 'Human' },
  ];

  readonly ownerTypeOptions = [
    { value: '', label: 'All owners' },
    { value: 'ai', label: 'AI' },
    { value: 'human', label: 'Human' },
    { value: 'unassigned', label: 'Unassigned' },
  ];

  readonly tabs: Array<{ value: TasksTabFilter; label: string }> = [
    { value: '', label: 'All' },
    { value: 'ai_tasks', label: 'AI tasks' },
    { value: 'human_tasks', label: 'Human tasks' },
    { value: 'awaiting_approval', label: 'Awaiting approval' },
    { value: 'scheduled_today', label: 'Scheduled today' },
    { value: 'overdue', label: 'Overdue' },
  ];

  /** Known buckets, in display order. Anything else the API sends is appended at runtime. */
  private readonly knownSummaryCards: Array<{ key: string; label: string; accent?: string }> = [
    { key: 'total', label: 'Total' },
    { key: 'awaiting_approval', label: 'Approval', accent: 'orange' },
    { key: 'scheduled', label: 'Scheduled', accent: 'orange' },
    { key: 'running', label: 'Running', accent: 'green' },
    { key: 'completed', label: 'Completed' },
    { key: 'paused', label: 'Paused' },
    { key: 'failed', label: 'Failed', accent: 'red' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  /** Non-status metadata on the summary payload — never rendered as a card. */
  private readonly summaryMetaKeys = ['is_exact', 'computed_at'];

  summaryCards: Array<{ key: string; label: string; accent?: string }> = this.knownSummaryCards.slice();

  constructor(
    private appService: AppService,
    private tasksService: TasksService,
    private jobService: JobService,
    private dialog: MatDialog,
    private notify: NotificationService,
    private enterpriseAdminService: EnterpriseAdminService,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.loadTeamMembers();
    this.loadOutcomes();
    this.loadTasks();
  }

  ngOnDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.subs.unsubscribe();
  }

  get entityId(): string {
    return this.profile && this.profile.entity && this.profile.entity.id;
  }

  get assigneeOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '', label: 'All assignees' },
      ...this.teamMembers.map((member) => ({
        value: this.getTeamMemberValue(member),
        label: this.getTeamMemberLabel(member),
      })),
    ];
  }

  get outcomeFilterOptions(): Array<{ value: string; label: string }> {
    return [
      { value: '', label: 'All outcomes' },
      ...this.outcomeOptions.map((outcome) => ({ value: outcome, label: outcome })),
    ];
  }

  get pageSizeSelectOptions(): Array<{ value: number; label: string }> {
    return this.pageSizeOptions.map((size) => ({
      value: size,
      label: `Rows ${size}`,
    }));
  }

  onProjectsLoaded(projects: any[]) {
    this.projectsList = projects;
    this.cd.markForCheck();
  }

  onProjectFilterChange(projectId: string | null) {
    this.selectedProjectId = projectId || '';
    this.onFilterChange();
  }

  onChannelFilterChange(value: string | number) {
    this.selectedChannel = String(value);
    this.onFilterChange();
  }

  onOwnerTypeFilterChange(value: string | number) {
    this.selectedOwnerType = String(value);
    this.onFilterChange();
  }

  onAssigneeFilterChange(value: string | number) {
    this.selectedOwnerId = String(value);
    this.onFilterChange();
  }

  onOutcomeFilterChange(value: string | number) {
    this.selectedOutcome = String(value);
    this.onFilterChange();
  }

  loadOutcomes() {
    if (!this.entityId) {
      return;
    }
    this.jobService.getHoaEntityOutcomesFilter(this.entityId).subscribe(
      (res: any) => {
        const list = (res && Array.isArray(res.outcomes)) ? res.outcomes : [];
        this.outcomeOptions = list
          .map((v: any) => (typeof v === 'string' ? v.trim() : ''))
          .filter((v: string) => !!v);
      },
      () => {
        this.outcomeOptions = [];
      }
    );
  }

  loadTeamMembers() {
    if (!this.entityId) {
      return;
    }
    this.jobService.searchAndSortTeamMembersv2(this.entityId, 0, 100, 'createdOn', 'desc', []).subscribe(
      (res: any) => {
        this.teamMembers = (res && res.content) || [];
      },
      () => {
        this.teamMembers = [];
      }
    );
  }

  loadTasks() {
    if (!this.entityId) {
      return;
    }
    this.isLoading = true;
    this.tasksService.getTasks({
      entity_id: this.entityId,
      project_id: this.selectedProjectId || undefined,
      status: this.selectedStatus || undefined,
      owner_type: this.selectedOwnerType || undefined,
      owner_id: this.selectedOwnerId || undefined,
      channel: this.selectedChannel || undefined,
      outcome: this.selectedOutcome || undefined,
      search: this.searchQuery.trim() || undefined,
      tab: this.activeTab || undefined,
      pageNum: this.pageNum,
      pageSize: this.pageSize,
    }).subscribe(
      (res) => {
        this.isLoading = false;
        this.tasks = (res && res.data) || [];
        if (res && res.summary) {
          this.summary = res.summary;
          this.rebuildSummaryCards();
        }
        this.applyPagination(res && res.pagination);
        this.cd.markForCheck();
      },
      (err) => {
        this.isLoading = false;
        this.tasks = [];
        this.applyPagination(null);
        const msg = (err && err.error && (err.error.error || err.error.message)) || 'Failed to load tasks.';
        this.notify.showMessage(msg, 3000);
      }
    );
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.pageNum = 1;
      this.loadTasks();
    }, 400);
  }

  onFilterChange() {
    this.pageNum = 1;
    this.loadTasks();
  }

  setTab(tab: TasksTabFilter) {
    this.activeTab = tab;
    this.pageNum = 1;
    if (tab === 'awaiting_approval') {
      this.selectedStatus = 'awaiting_approval';
    } else if (tab === 'ai_tasks' || tab === 'human_tasks' || tab === 'scheduled_today' || tab === 'overdue') {
      this.selectedStatus = '';
    }
    this.loadTasks();
  }

  onSummaryCardClick(key: string) {
    if (key === 'total') {
      this.selectedStatus = '';
      this.activeTab = '';
    } else {
      this.selectedStatus = key;
      this.activeTab = key === 'awaiting_approval' ? 'awaiting_approval' : '';
    }
    this.pageNum = 1;
    this.loadTasks();
  }

  isSummaryActive(key: string): boolean {
    if (key === 'total') {
      return !this.selectedStatus && !this.activeTab;
    }
    return this.selectedStatus === key;
  }

  getSummaryValue(key: string): number {
    const value = this.summary && this.summary[key];
    return typeof value === 'number' ? value : 0;
  }

  /**
   * Renders every status bucket the API returns, not just the ones known at build
   * time — a newly added bucket (e.g. `cancelled`) shows up instead of silently
   * disappearing from the summary row.
   */
  private rebuildSummaryCards(): void {
    const cards = this.knownSummaryCards.slice();
    const known = new Set(cards.map((card) => card.key));

    Object.keys(this.summary || {}).forEach((key) => {
      if (known.has(key) || this.summaryMetaKeys.indexOf(key) !== -1) {
        return;
      }
      if (typeof this.summary[key] !== 'number') {
        return;
      }
      cards.push({ key, label: this.humanizeSummaryKey(key) });
    });

    this.summaryCards = cards;
  }

  private humanizeSummaryKey(key: string): string {
    return key
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  onPageSizeChange(size: string | number) {
    this.pageSize = Number(size);
    this.pageNum = 1;
    this.loadTasks();
  }

  onPageChange(page: number) {
    const target = Math.max(1, Math.min(page, this.totalPages));
    if (target === this.pageNum || this.isLoading) {
      return;
    }
    this.pageNum = target;
    this.loadTasks();
  }

  /**
   * The API returns snake_case paging (total_records / total_pages / has_next_page).
   * camelCase keys are read as a fallback only.
   */
  private applyPagination(pagination: any): void {
    const num = (value: any): number | null => {
      if (value == null || value === '') {
        return null;
      }
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    };

    if (!pagination) {
      this.totalElements = this.tasks.length;
      this.serverTotalPages = 0;
      this.hasNextPage = false;
      this.hasPreviousPage = this.pageNum > 1;
      this.isApproximateTotal = false;
      return;
    }

    const total = num(pagination.total_records) != null
      ? num(pagination.total_records)
      : num(pagination.totalElements);
    this.totalElements = total != null ? total : this.tasks.length;

    const totalPages = num(pagination.total_pages) != null
      ? num(pagination.total_pages)
      : num(pagination.totalPages);
    this.serverTotalPages = totalPages != null ? totalPages : 0;

    // Echo the page the server actually served, so the indicator can't drift.
    const currentPage = num(pagination.current_page) != null
      ? num(pagination.current_page)
      : num(pagination.pageNum);
    if (currentPage != null && currentPage > 0) {
      this.pageNum = currentPage;
    }

    const size = num(pagination.page_size) != null
      ? num(pagination.page_size)
      : num(pagination.pageSize);
    if (size != null && size > 0) {
      this.pageSize = size;
    }

    this.hasNextPage = pagination.has_next_page != null
      ? pagination.has_next_page === true
      : this.pageNum < this.totalPages;
    this.hasPreviousPage = pagination.has_previous_page != null
      ? pagination.has_previous_page === true
      : this.pageNum > 1;
    this.isApproximateTotal = pagination.is_approximate === true;
  }

  get totalPages(): number {
    if (this.serverTotalPages > 0) {
      return this.serverTotalPages;
    }
    return Math.max(1, Math.ceil(this.totalElements / this.pageSize));
  }

  get showingLabel(): string {
    if (!this.totalElements) {
      return 'Showing 0';
    }
    const start = (this.pageNum - 1) * this.pageSize + 1;
    const end = Math.min(this.pageNum * this.pageSize, this.totalElements);
    const total = (this.isApproximateTotal ? '~' : '') + this.totalElements.toLocaleString();
    return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total}`;
  }

  openCreateTask(mode: 'ai' | 'human') {
    const ensureProjects = this.projectsList.length
      ? Promise.resolve(this.projectsList)
      : this.jobService.getAllProjects(this.entityId, 1, 100, 'created_at', 'desc', []).toPromise()
          .then((res: any) => {
            const projects = (res && res.items) || [];
            this.projectsList = Array.isArray(projects) ? projects : [];
            return this.projectsList;
          })
          .catch(() => this.projectsList);

    ensureProjects.then((projects) => {
      const dialogRef = this.dialog.open(CreateTaskDialogComponent, {
        width: '600px',
        maxHeight: '90vh',
        panelClass: ['add-edit-staff-dialog', 'create-task-dialog-panel'],
        disableClose: true,
        data: {
          mode,
          entityId: this.entityId,
          projects: projects || [],
          defaultProjectId: this.selectedProjectId || null,
          defaultOutcome: this.selectedOutcome || null,
          outcomeOptions: this.outcomeOptions,
        }
      });
      dialogRef.afterClosed().subscribe((saved) => {
        if (saved) {
          this.loadTasks();
        }
      });
    });
  }

  openEditTask(task: TaskListItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!task || !task.task_id) {
      return;
    }
    const dialogRef = this.dialog.open(CreateTaskDialogComponent, {
      width: '600px',
      maxHeight: '90vh',
      panelClass: ['add-edit-staff-dialog', 'create-task-dialog-panel'],
      disableClose: true,
      data: {
        mode: 'edit',
        entityId: this.entityId,
        projects: this.projectsList,
        editTask: task,
      }
    });
    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        if (this.selectedTaskId === task.task_id) {
          this.openTaskDetail({ task_id: task.task_id } as TaskListItem);
        }
        this.loadTasks();
      }
    });
  }

  getTeamMemberLabel(member: any): string {
    return getTeamMemberDisplayName(member);
  }

  getTeamMemberValue(member: any): string {
    return getTeamMemberUserId(member);
  }

  openTaskDetail(task: TaskListItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!task || !task.task_id) {
      return;
    }
    this.selectedTaskId = task.task_id;
    this.drawerOpen = true;
    this.drawerTab = 'details';
    this.candidateDetails = null;
    this.activityHistoryLoading = false;
    this.detailLoading = true;
    this.taskDetail = null;
    this.tasksService.getTask(task.task_id).subscribe(
      (detail) => {
        this.detailLoading = false;
        this.taskDetail = detail;
        this.cd.markForCheck();
      },
      () => {
        this.detailLoading = false;
        this.taskDetail = task as TaskDetailResponse;
      }
    );
  }

  closeDrawer() {
    this.drawerOpen = false;
    this.selectedTaskId = null;
    this.taskDetail = null;
    this.drawerTab = 'details';
    this.candidateDetails = null;
    this.activityHistoryLoading = false;
  }

  setDrawerTab(tab: TasksDrawerTab) {
    this.drawerTab = tab;
    if (tab === 'activity' && !this.candidateDetails && !this.activityHistoryLoading) {
      this.loadActivityHistory();
    }
  }

  getDrawerProjectId(): string {
    const d = this.taskDetail;
    if (!d) {
      return '';
    }
    return d.project_id || (d.lead && d.lead.project_id) || '';
  }

  canLoadActivityHistory(): boolean {
    const d = this.taskDetail;
    if (!d) {
      return false;
    }
    const peopleId = d.people_id || (d.lead && d.lead.people_id);
    const projectId = this.getDrawerProjectId();
    const clientNumber = d.lead_number || (d.lead && d.lead.client_number);
    return !!(peopleId && projectId && clientNumber && clientNumber !== 'Reveal');
  }

  loadActivityHistory() {
    const d = this.taskDetail;
    if (!d || !this.canLoadActivityHistory()) {
      this.candidateDetails = null;
      return;
    }

    const peopleId = d.people_id || (d.lead && d.lead.people_id);
    const projectId = this.getDrawerProjectId();
    const clientNumber = d.lead_number || (d.lead && d.lead.client_number);

    this.activityHistoryLoading = true;
    this.enterpriseAdminService.fetchCandidatesDeatails(peopleId, projectId, clientNumber).subscribe(
      (res) => {
        this.activityHistoryLoading = false;
        this.candidateDetails = res;
        this.cd.markForCheck();
      },
      () => {
        this.activityHistoryLoading = false;
        this.candidateDetails = null;
        this.cd.markForCheck();
      }
    );
  }

  approveTask(task: TaskListItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.openTaskActionDialog(task, 'approve', (result) =>
      this.tasksService.approveTask(task.task_id, {
        when: result.when,
        scheduled_at: result.scheduled_at,
      }), 'Task approved.');
  }

  rejectTask(task: TaskListItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.openTaskActionDialog(task, 'reject', (result) =>
      this.tasksService.rejectTask(task.task_id, result.reason), 'Task rejected.');
  }

  pauseTask(task: TaskListItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.openTaskActionDialog(task, 'pause', (result) =>
      this.tasksService.pauseTask(task.task_id, result.reason), 'Task paused.');
  }

  completeTask(task: TaskListItem, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    const jobId = task.job_id;
    if (!jobId) {
      this.notify.showMessage('Task is missing job reference.', 3000);
      return;
    }
    this.openTaskActionDialog(task, 'complete', (result) => {
      if (!result.reason) {
        throw new Error('Reason required');
      }
      return this.jobService.updateTaskCompletion(jobId, task.task_id, result.reason, this.entityId);
    }, 'Task marked complete.', true);
  }

  private openTaskActionDialog(
    task: TaskListItem,
    mode: TaskActionDialogMode,
    action: (result: TaskActionDialogResult) => any,
    successMsg: string,
    closeDrawerOnSuccess = false,
  ) {
    const dialogRef = this.dialog.open(TaskActionDialogComponent, {
      width: '520px',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: {
        mode,
        taskTitle: task.title,
        leadName: task.lead_name,
        scheduledAt: task.scheduled_at,
      },
    });

    dialogRef.afterClosed().subscribe((result: TaskActionDialogResult) => {
      if (!result || !result.confirmed) {
        return;
      }
      this.isActionLoading = true;
      action(result).subscribe(
        () => {
          this.isActionLoading = false;
          this.notify.showMessage(successMsg, 2000);
          if (closeDrawerOnSuccess) {
            this.closeDrawer();
          } else if (this.selectedTaskId) {
            this.openTaskDetail({ task_id: this.selectedTaskId } as TaskListItem);
          }
          this.loadTasks();
        },
        (err: any) => {
          this.isActionLoading = false;
          const msg = (err && err.error && (err.error.error || err.error.message)) || 'Action failed.';
          this.notify.showMessage(msg, 3000);
        }
      );
    });
  }

  /** Always includes the year, so a task from last September can't pass for this one. */
  formatWhen(value: string): string {
    return formatTaskDateTime(value);
  }

  /** Status that accounts for the task's date — see task-status.util.ts. */
  effective(task: TaskListItem | TaskDetailResponse): EffectiveTaskStatus {
    return getEffectiveTaskStatus(task as any);
  }

  effectiveClass(task: TaskListItem | TaskDetailResponse): string {
    return `task-badge task-badge--tone-${this.effective(task).tone}`;
  }

  formatStatus(status: string): string {
    if (!status) {
      return '—';
    }
    return status.replace(/_/g, ' ').toUpperCase();
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'running') return 'task-badge task-badge--running';
    if (s === 'awaiting_approval') return 'task-badge task-badge--approval';
    if (s === 'failed') return 'task-badge task-badge--failed';
    if (s === 'completed') return 'task-badge task-badge--completed';
    if (s === 'scheduled') return 'task-badge task-badge--scheduled';
    if (s === 'paused') return 'task-badge task-badge--paused';
    return 'task-badge';
  }

  getOwnerClass(ownerType: string): string {
    const t = (ownerType || '').toLowerCase();
    if (t === 'ai') return 'task-badge task-badge--ai';
    if (t === 'human') return 'task-badge task-badge--human';
    return 'task-badge task-badge--muted';
  }

  getChannelLabel(channel: string): string {
    if (!channel) return '—';
    return channel.toUpperCase();
  }

  getTypeLabel(taskType: string): string {
    if (!taskType) return '—';
    return taskType.replace(/_/g, ' ').toUpperCase();
  }

  getDrawerLeadName(): string {
    const d = this.taskDetail;
    if (!d) return '—';
    return d.lead_name || (d.lead && d.lead.client_name) || '—';
  }

  getDrawerLeadNumber(): string {
    const d = this.taskDetail;
    if (!d) return '';
    return d.lead_number || (d.lead && d.lead.client_number) || '';
  }

  getDrawerOwnerLabel(): string {
    const d = this.taskDetail;
    if (!d) return '—';
    const type = (d.owner_type || '').toUpperCase();
    const name = d.owner_name || '';
    if (type && name) {
      return `${type} · ${name}`;
    }
    return name || type || '—';
  }

  getTaskReason(): string {
    const d = this.taskDetail;
    if (!d) return '';
    return (d.title || d.instructions || '').trim();
  }

  isAwaitingApproval(task: TaskListItem | TaskDetailResponse | null): boolean {
    if (!task) {
      return false;
    }
    return (task.status || '').toLowerCase() === 'awaiting_approval';
  }

  canShowApprove(task: TaskListItem | TaskDetailResponse | null): boolean {
    if (!task) {
      return false;
    }
    return this.isAwaitingApproval(task) || !!(task.actions && task.actions.can_approve);
  }

  canShowReject(task: TaskListItem | TaskDetailResponse | null): boolean {
    if (!task) {
      return false;
    }
    return this.isAwaitingApproval(task) || !!(task.actions && task.actions.can_reject);
  }

  canShowPause(task: TaskListItem | TaskDetailResponse | null): boolean {
    if (!task) {
      return false;
    }
    return !!(task.actions && task.actions.can_pause);
  }

  hasDrawerActions(): boolean {
    const d = this.taskDetail;
    if (!d) {
      return false;
    }
    return this.canShowApprove(d)
      || this.canShowReject(d)
      || this.canShowPause(d)
      || !!(d.actions && d.actions.can_complete);
  }

  getAiMemoryLines(): string[] {
    const d = this.taskDetail;
    if (!d) return [];

    const lines: string[] = [];
    const mem = d.ai_memory;

    if (mem) {
      if (Array.isArray(mem)) {
        mem.forEach((line) => {
          if (line != null && String(line).trim()) {
            lines.push(String(line).trim());
          }
        });
      } else if (typeof mem === 'object') {
        Object.keys(mem).forEach((key) => {
          const value = mem[key];
          if (value != null && String(value).trim()) {
            lines.push(`${this.formatMemoryKey(key)}: ${value}`);
          }
        });
      } else if (String(mem).trim()) {
        String(mem).split('\n').forEach((line) => {
          if (line.trim()) {
            lines.push(line.trim());
          }
        });
      }
    }

    const lead = d.lead;
    if (lead) {
      if (lead.overall_status && !lines.some((l) => l.includes(lead.overall_status))) {
        lines.push(`Lead status: ${lead.overall_status}`);
      }
      const orch = lead.ai_orchestration;
      if (orch) {
        if (orch.last_outbound_channel && orch.last_outbound_at) {
          lines.push(`Last contact: ${this.formatUnixTime(orch.last_outbound_at)} via ${this.getChannelLabel(orch.last_outbound_channel)}`);
        }
        const planner = orch.last_planner_output;
        if (planner && planner.reason) {
          lines.push(planner.reason);
        }
        if (planner && planner.task_brief && planner.task_brief.why) {
          lines.push(planner.task_brief.why);
        }
        if (planner && planner.task_brief && planner.task_brief.accomplish) {
          lines.push(`Goal: ${planner.task_brief.accomplish}`);
        }
      }
    }

    if (d.instructions && d.title && d.instructions !== d.title && !lines.includes(d.instructions)) {
      lines.push(d.instructions);
    }

    return lines;
  }

  private formatMemoryKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getTimelineItems(): any[] {
    return (this.taskDetail && this.taskDetail.timeline) || [];
  }

  formatTimelineTime(item: any): string {
    if (!item) return '—';
    const raw = item.start_time || item.time || item.timestamp;
    if (raw == null || raw === '') return '—';
    const ts = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (typeof ts === 'number' && !isNaN(ts) && ts > 0) {
      return formatStartTimeLabel(ts);
    }
    return this.formatWhen(String(raw));
  }

  getTimelineChannel(item: any): string {
    if (!item) return '—';
    const type = item.conversation_type || item.channel || item.type;
    if (type) {
      const normalized = String(type).toLowerCase();
      if (normalized === 'call' || normalized === 'phone') return 'Call';
      if (normalized === 'whatsapp' || normalized === 'wa') return 'WA';
      if (normalized === 'email') return 'Email';
      return this.getChannelLabel(String(type));
    }
    if (item.status) {
      return String(item.status);
    }
    return 'Activity';
  }

  getTimelineDetail(item: any): string {
    if (!item) return '';
    const parts: string[] = [];
    if (item.status && item.conversation_type) {
      parts.push(String(item.status));
    } else if (item.status && !item.conversation_type) {
      // status is the main label when channel is unknown
    }
    if (item.duration) {
      parts.push(String(item.duration));
    }
    if (item.job_id && !parts.length) {
      parts.push('Logged');
    }
    return parts.join(' · ');
  }

  private formatUnixTime(value: number | string): string {
    const ts = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof ts !== 'number' || isNaN(ts) || ts <= 0) {
      return '—';
    }
    return formatStartTimeLabel(ts);
  }
}
